import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Viewer } from '@ydrive/gs-viewer';

// Demo file constant - change this to update the marketing demo scene
const DEMO_GAUS_FILE = '/OfficeSpace.gaus';

// Type definitions
interface Embedded3DViewerProps {
    isOpen: boolean;
    onClose: () => void;
}

const Embedded3DViewer: React.FC<Embedded3DViewerProps> = ({ 
    isOpen, 
    onClose
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const modelContainerRef = useRef<HTMLDivElement>(null);
    const progressTextRef = useRef<HTMLParagraphElement>(null);
    const viewerRef = useRef<any>(null);

    // Load the 3D model
    const load3DModel = async () => {
        if (!Viewer) {
            setError('3D Viewer library not available');
            return;
        }

        try {
            console.log('[Embedded3DViewer] Loading 3D model:', DEMO_GAUS_FILE);
            
            // Cleanup any existing viewer
            if (viewerRef.current && viewerRef.current !== 'cleaning') {
                try {
                    viewerRef.current.dispose();
                } catch (error) {
                    console.warn('[Embedded3DViewer] Error disposing previous viewer:', error);
                }
                viewerRef.current = null;
            }
            
            setLoading(true);
            setError(null);
            
            // Wait for container to be ready
            await new Promise(resolve => setTimeout(resolve, 300));
            
            if (!modelContainerRef.current) {
                throw new Error('3D viewer container not found');
            }
            
            console.log('[Embedded3DViewer] Container ready, creating viewer...');
            
            // Create viewer
            const viewer = new Viewer({
                sharedMemoryForWorkers: false,
                rootElement: modelContainerRef.current
            });
            viewerRef.current = viewer;
            
            // Track progress
            let lastProgress = 0;
            
            const splatBufferOptions = {
                showLoadingUI: false,
                onProgress: (progress: number) => {
                    if (progress < lastProgress) return;
                    lastProgress = progress;

                    console.log(`[Embedded3DViewer] Loading progress: ${progress}%`);
                    if (progressTextRef.current) {
                        progressTextRef.current.textContent = `Loading model... ${Math.round(progress)}%`;
                    }
                }
            };

            console.log('[Embedded3DViewer] Adding splat scene...');
            await viewer.addSplatScene(DEMO_GAUS_FILE, splatBufferOptions);
            
            console.log('[Embedded3DViewer] Starting viewer...');
            viewer.start();
            
            setLoading(false);
            console.log('[Embedded3DViewer] 3D viewer loaded successfully');
            
        } catch (error) {
            console.error('[Embedded3DViewer] Error loading 3D viewer:', error);
            setLoading(false);
            setError(error instanceof Error ? error.message : 'Unknown error occurred');
        }
    };

    // Handle escape key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                event.preventDefault();
                event.stopPropagation();
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, onClose]);

    // Load model when viewer opens
    useEffect(() => {
        if (isOpen) {
            load3DModel();
        }
    }, [isOpen]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (viewerRef.current && viewerRef.current !== 'cleaning') {
                try {
                    if (typeof viewerRef.current.dispose === 'function') {
                        viewerRef.current.dispose();
                    }
                } catch (error) {
                    console.warn('[Embedded3DViewer] Cleanup error:', error);
                }
            }
        };
    }, []);

    // Don't render if not open
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 w-full h-full bg-black z-[9999]">
            {/* Close button */}
            <div className="fixed top-4 right-4 z-[10001]">
                <button
                    onClick={onClose}
                    className="bg-[#161513]/90 backdrop-blur-sm text-white px-6 py-3 shadow-lg hover:bg-[#161513] transition-all duration-200 flex items-center gap-3 font-medium border border-[#0CB4E4]/30 rounded-xl"
                >
                    <X className="w-5 h-5" />
                    <span>Close</span>
                    <span className="text-xs text-gray-400">(ESC)</span>
                </button>
            </div>

            {/* Brand overlay */}
            <div className="fixed top-4 left-4 z-[10001] flex items-center gap-2">
                <img src="/logo.gif" alt="CR3ATE.AI" className="h-10 w-10 object-contain" />
                <h1 className="font-light text-xl text-[#0CB4E4] font-brand">
                    CR3ATE.AI
                </h1>
            </div>

            {/* Loading spinner */}
            {loading && (
                <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm z-[10002]">
                    <div className="animate-spin h-16 w-16 border-4 border-[#0CB4E4] border-t-transparent mb-6"></div>
                    <p ref={progressTextRef} className="text-white text-lg font-medium">
                        Loading 3D model...
                    </p>
                    <p className="text-white/70 text-sm mt-2">
                        Experience the future of 3D reconstruction
                    </p>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm z-[10002]">
                    <div className="bg-[#161513] p-8 rounded-xl border border-red-500/30 max-w-md text-center">
                        <p className="text-red-400 text-lg mb-4">Failed to load 3D model</p>
                        <p className="text-gray-300 mb-6">{error}</p>
                        <div className="flex gap-4 justify-center">
                            <button
                                onClick={load3DModel}
                                className="px-6 py-3 bg-[#0C5484] text-white rounded-lg hover:bg-[#0CB4E4]/80 transition-colors"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={onClose}
                                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 3D Model Viewer Container */}
            <div
                ref={modelContainerRef}
                className="absolute inset-0 w-full h-full"
                style={{
                    zIndex: 10000,
                    backgroundColor: "black"
                }}
            />

            {/* Navigation instructions */}
            {!loading && !error && (
                <div className="fixed bottom-4 left-4 z-[10001] bg-[#161513]/90 backdrop-blur-sm rounded-xl p-4 border border-[#0CB4E4]/30">
                    <h5 className="text-sm font-medium text-white mb-2">Navigation</h5>
                    <div className="text-xs text-gray-300 space-y-1">
                        <p>Click and drag to rotate</p>
                        <p>Scroll to zoom</p>
                        <p>Right-click + drag to pan</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Embedded3DViewer;